import React, { FunctionComponent, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ButtonV2 as Button, usePermission } from 'portal-commons';

import { selectCountries } from '../../../CSP-Form/selectors';
import { Brand } from '@/apis/core';

import {
  faAddressBook,
  faBuildingCircleCheck,
  faCircleUser,
  faPen,
} from '@fortawesome/pro-regular-svg-icons';

import { DetailsSection, InfoRow, Divider } from './components';

interface CompanyDetailsTabProps {
  brand: Brand;
  editable?: boolean;
  onEdit?: () => void;
}

const CompanyDetailsTab: FunctionComponent<CompanyDetailsTabProps> = ({
  brand,
  editable = false,
  onEdit,
}) => {
  const { hasPermission } = usePermission();
  const countries = useSelector(selectCountries);
  const einIssuingCountry = useMemo(
    () =>
      countries.find((item) => item.value === brand.einIssuingCountry)?.label ??
      brand.einIssuingCountry,
    [countries, brand.einIssuingCountry]
  );
  const country = useMemo(
    () =>
      countries.find((item) => item.value === brand.country)?.label ??
      brand.country,
    [countries, brand.country]
  );

  return (
    <>
      <DetailsSection
        title="Company Details"
        icon={faBuildingCircleCheck}
        testId="companyDetails"
        action={
          <Button
            type="button"
            size="lg"
            variant="transparent"
            startAdornment={faPen}
            color="primary"
            shape="square"
            onClick={onEdit}
            disabled={
              !hasPermission('brandDetail.editCompanyDetails') || !editable
            }
            data-testid="editBrandCompanyDetailsBtn"
          >
            Edit
          </Button>
        }
      >
        <InfoRow col={3}>
          <InfoRow.Item label="Brand DBA Name" testId="displayName">
            {brand.displayName}
          </InfoRow.Item>
          <InfoRow.Item
            label="Company Registration Number / ID Issuing Country"
            testId="einIssuingCountry"
          >
            {einIssuingCountry}
          </InfoRow.Item>
          <InfoRow.Item label="Address / Street" testId="street">
            {brand.street}
          </InfoRow.Item>
        </InfoRow>
        <InfoRow col={3}>
          <InfoRow.Item label="City" testId="city">
            {brand.city}
          </InfoRow.Item>
          <InfoRow.Item label="State / Region" testId="state">
            {brand.state}
          </InfoRow.Item>
          <InfoRow.Item label="Country" testId="country">
            {country}
          </InfoRow.Item>
        </InfoRow>
        <InfoRow col={3}>
          <InfoRow.Item label="Postal Code" testId="postalCode">
            {brand.postalCode}
          </InfoRow.Item>
          <InfoRow.Item label="Stock Symbol" testId="stockSymbol">
            {brand.stockSymbol}
          </InfoRow.Item>
          <InfoRow.Item label="Stock Exchange" testId="stockExchange">
            {brand.stockExchange}
          </InfoRow.Item>
        </InfoRow>
      </DetailsSection>

      <Divider />

      <DetailsSection
        title="Support Contact Details"
        icon={faCircleUser}
        testId="supportContactDetails"
      >
        <InfoRow col={3}>
          <InfoRow.Item label="Support Email Address" testId="email">
            {brand.email}
          </InfoRow.Item>
          <InfoRow.Item label="Support Phone Number" testId="phone">
            {brand.phone}
          </InfoRow.Item>
        </InfoRow>
      </DetailsSection>

      <Divider />

      <DetailsSection
        title="Business Contact Details"
        icon={faAddressBook}
        testId="businessContactDetails"
      >
        <InfoRow col={4}>
          <InfoRow.Item label="Individual's Name" testId="businessContactName">
            {brand.businessContactFirstName || brand.businessContactLastName
              ? `${brand.businessContactFirstName} ${brand.businessContactLastName}`.trim()
              : 'N/A'}
          </InfoRow.Item>
          <InfoRow.Item
            label="Business Email Address"
            testId="businessContactEmail"
          >
            {brand.businessContactEmail || 'N/A'}
          </InfoRow.Item>
          <InfoRow.Item label="Title" testId="businessContactTitle">
            {brand.businessContactTitle || 'N/A'}
          </InfoRow.Item>
          <InfoRow.Item label="Auth+ Compliant" testId="authplusCompliant">
            {brand.businessContactEmailVerifiedDate ? 'Yes' : 'No'}
          </InfoRow.Item>
        </InfoRow>
      </DetailsSection>
    </>
  );
};

export default CompanyDetailsTab;
